import { useState } from 'react';
import styled from 'styled-components';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { SectionContainer } from 'components/DetailsView/Section';
import { TotemLightBox, type TotemLightboxImage } from 'components/TotemLightBox';
import { type GetTicketCommentsQuery } from 'data/__generated__';

import { formatTicketDatetime } from '../helpers';

export const TicketCommentsSection = ({
    comments,
    loading,
    error,
    onRetry,
}: {
    comments: GetTicketCommentsQuery['ticketComments'] | undefined;
    loading: boolean;
    error: boolean;
    onRetry: () => void;
}) => {
    const [isLightboxOpen, setIsLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState<TotemLightboxImage[]>([]);
    const [initialImageIndex, setInitialImageIndex] = useState(0);

    const handlePhotoClick = (imageUrls: string[], photoIndex: number) => {
        setLightboxImages(imageUrls.map((url) => ({ url })));
        setInitialImageIndex(photoIndex);
        setIsLightboxOpen(true);
    };

    return (
        <SectionContainer title="Échanges Odoo" isInitiallyOpen>
            <>
                {loading ? <EmptyMessage>Chargement du fil de discussion…</EmptyMessage> : null}
                {error ? (
                    <ErrorBlock>
                        <EmptyMessage>Fil indisponible pour le moment.</EmptyMessage>
                        <TotemPrimaryButton $isSecondaryStyle onClick={onRetry}>
                            Réessayer
                        </TotemPrimaryButton>
                    </ErrorBlock>
                ) : null}
                {!loading && !error && (comments ?? []).length === 0 ? (
                    <EmptyMessage>Aucun échange Odoo.</EmptyMessage>
                ) : null}
                {!loading && !error ? (
                    <CommentsList data-test="ticket-comments">
                        {(comments ?? []).map((comment) => (
                            <CommentItem key={comment.odooMessageId}>
                                <CommentMeta>
                                    {comment.authorName ?? 'Odoo'} — {formatTicketDatetime(comment.postedAt)}
                                </CommentMeta>
                                {comment.body ? <CommentBody>{comment.body}</CommentBody> : null}
                                {comment.imageUrls.length > 0 ? (
                                    <Photos>
                                        {comment.imageUrls.map((imageUrl, photoIndex) => (
                                            <Photo
                                                key={imageUrl}
                                                src={imageUrl}
                                                alt="Photo du fil Odoo"
                                                onClick={() => handlePhotoClick(comment.imageUrls, photoIndex)}
                                            />
                                        ))}
                                    </Photos>
                                ) : null}
                            </CommentItem>
                        ))}
                    </CommentsList>
                ) : null}
                <TotemLightBox
                    images={lightboxImages}
                    initialImageIndex={initialImageIndex}
                    isOpen={isLightboxOpen}
                    setIsOpen={setIsLightboxOpen}
                />
            </>
        </SectionContainer>
    );
};

const CommentsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CommentItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const CommentMeta = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.infoTextColor};
`;

const CommentBody = styled.span`
    font-size: 14px;
    white-space: pre-wrap;
    line-height: 1.4;
`;

const Photos = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 4px;
`;

const Photo = styled.img`
    max-width: 160px;
    max-height: 160px;
    object-fit: contain;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        opacity: 0.8;
    }
`;

const EmptyMessage = styled.div`
    padding: 8px 0;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ErrorBlock = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
`;
